import { useEffect, useState } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import api from '../../lib/api';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Loader2, FileText } from 'lucide-react';

export default function TemplatesTab({ meta }) {
  const [templates, setTemplates] = useState([]);
  const [dialog, setDialog] = useState(null);

  const load = () => api.get('/comms/templates').then((r) => setTemplates(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const del = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    await api.delete(`/comms/templates/${t.id}`);
    toast.success('Template deleted'); load();
  };

  return (
    <div className="space-y-4" data-testid="templates-tab">
      <div className="flex justify-end">
        <Button className="bg-magenta hover:bg-magenta/90" size="sm" onClick={() => setDialog({ tpl: null })} data-testid="new-template-btn">
          <Plus className="w-4 h-4 mr-1.5" /> New Template
        </Button>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
        {templates.map((t) => (
          <Card key={t.id} data-testid={`template-card-${t.id}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5 font-semibold text-navy text-sm"><FileText className="w-4 h-4 text-magenta shrink-0" /> {t.name}</div>
                  <div className="text-[11px] text-slate-400 uppercase mt-0.5">{t.category || 'general'}</div>
                  <div className="text-xs text-slate-500 mt-1.5 line-clamp-2">{t.subject}</div>
                </div>
                <div className="flex gap-0.5 shrink-0">
                  <Button size="sm" variant="ghost" onClick={() => setDialog({ tpl: t })} data-testid={`edit-template-${t.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                  {meta.is_admin && <Button size="sm" variant="ghost" className="text-red-500" onClick={() => del(t)} data-testid={`delete-template-${t.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      {dialog && <TemplateDialog tpl={dialog.tpl} onClose={() => { setDialog(null); load(); }} />}
    </div>
  );
}

function TemplateDialog({ tpl, onClose }) {
  const [form, setForm] = useState({
    name: tpl?.name || '', category: tpl?.category || '', subject: tpl?.subject || '',
    preview_text: tpl?.preview_text || '', html_body: tpl?.html_body || '',
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!form.name.trim()) { toast.error('Template name required'); return; }
    setBusy(true);
    try {
      if (tpl) await api.put(`/comms/templates/${tpl.id}`, form);
      else await api.post('/comms/templates', form);
      toast.success('Template saved');
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="template-dialog">
        <DialogHeader><DialogTitle>{tpl ? 'Edit Template' : 'New Template'}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="template-name-input" /></div>
          <div><Label className="text-xs">Category</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="newsletter, event…" data-testid="template-category-input" /></div>
          <div className="col-span-2"><Label className="text-xs">Default Subject</Label><Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="template-subject-input" /></div>
        </div>
        <div>
          <Label className="text-xs">Body</Label>
          <div className="border border-slate-200 rounded-lg overflow-hidden mt-1">
            <ReactQuill theme="snow" value={form.html_body} onChange={(v) => setForm({ ...form, html_body: v })} />
          </div>
        </div>
        <Button className="bg-magenta hover:bg-magenta/90 w-full" onClick={save} disabled={busy} data-testid="save-template-btn">
          {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />} Save Template
        </Button>
      </DialogContent>
    </Dialog>
  );
}
